/**
 * Shared event types for the S2 `event/admin/` contract.
 *
 * v2 defines these ad-hoc inside each component, which is how its `EventData`
 * type drifted out of sync with the API (it declares a flat
 * `Approval.FirstReminderDays` / `MedicalCheck`, but the API actually returns
 * the nested `Approval.RSVP.ReminderDays[]` / `Approval.Checks.Medical` shape
 * that v2's own EventPermissions component reads). These types follow the real
 * runtime shape — verified against v2's components, not its type aliases.
 *
 * PII WARNING: `Attendee` carries student names, Edumate student numbers, carer
 * emails/phones and medical-form state. Never log it, never put any of it in a
 * URL, never persist it to browser storage. See CLAUDE.md.
 */

import type { PublishStatus } from "../hooks/useEventPublish";

export interface EventLocation {
	Name?: string | null;
	Address?: string | null;
	/**
	 * Null, not absent, is what the API sends for an event with no coordinates —
	 * so these have to be `| null` rather than just optional. Typing them without
	 * it let `null` through a `!== undefined` guard and put the literal string
	 * "null,null" into a map URL.
	 */
	Lat?: number | string | null;
	Lon?: number | string | null;
}

export interface EventApproval {
	Required?: boolean;
	Compulsory?: boolean;
	/* Null, not absent, is what the API stores for anything never set (an event
	   with no schedule holds `ReminderDays: [null, null]`), so these are
	   `| null` and read with a type guard rather than trusted as a number. */
	Checks?: {
		Medical?: boolean | null;
		EmergencyContact?: boolean | null;
		Dietary?: boolean | null;
	};
	RSVP?: {
		LockDays?: number | null;
		ReminderDays?: (number | null)[];
	};
	TermsAndConditions?: string | null;
	ExternalPermissionForm?: string | null;
	ExternalMedicalForm?: string | null;
	Urls?: {
		MoreInfo?: string | null;
		Event?: string | null;
	};
}

/** The banner AS STORED on an event. */
export interface EventBanner {
	_id?: string | null;
	ImageUrl?: string | null;
}

/**
 * One banner as the LIBRARY lists it — `GET event/admin/banners/`.
 *
 * A DIFFERENT SHAPE from `EventBanner`, and that is the whole reason this type
 * exists. The list endpoint answers `{id, img}`; the field it is chosen into
 * answers `{_id, ImageUrl}`. The picker read the stored names off the list
 * response, so every tile had `src={undefined}` and selecting one wrote a
 * banner of two undefineds. Naming both shapes makes that mistake a type error
 * rather than a blank grid.
 */
export interface BannerOption {
	id: string;
	img: string;
}

/**
 * A group the event was built from (`EventDoc.SelectedGroups`).
 *
 * `Label` — capital L — is what the event document actually stores, and what
 * event-manager-v2 both writes and reads. This app wrote a lowercase `label`
 * for a while, which is why the reader below accepts either: a group added by
 * the older build is on real events and would otherwise render as a nameless
 * row with a bin beside it. New writes use v2's shape so the two apps agree.
 */
export interface EventGroup {
	_id: string;
	id?: string;
	Label?: string;
	/** Legacy: written by this app before the casing mismatch was found. */
	label?: string;
	type?: string;
	Icon?: string;
	MemberCount?: number;
	/** Present on stored records; the editor never populates it. */
	Members?: unknown[];
}

/**
 * One of the event's own audience groups (see `EventDoc.EventGroups`).
 *
 * `_id` is the group key everything downstream expects: notice-manager reads
 * `group_id` from the URL and looks each one up via `group/search/?id=`, and
 * `group/admin/notify/` takes the same ids. `GroupId` is the separate numeric
 * Edumate identifier and is not interchangeable with it.
 */
export interface EventAudienceGroup {
	_id: string;
	GroupId?: number;
	Label?: string;
	Icon?: string | null;
	Visibility?: string;
}

/**
 * A notice, as the event document embeds it and as `notice/admin/` returns it.
 *
 * Every field past the id is optional because the two sources disagree about
 * which of them they send, and about what they call the same thing: the search
 * answers `PublishAt`, the event document `Date`, and the notice list view
 * `PublishedAt`. The card reads whichever arrived rather than picking a winner
 * — see `noticeDate` in EventNoticesTab.
 */
export interface EventNotice {
	_id: string;
	Title?: string;
	Summary?: string;
	MainImage?: string;
	Thumbnail?: string;
	PublishedAt?: string;
	PublishAt?: string;
	Date?: string;
	/** S2's slim author projection: just enough to draw a face and a name. */
	AuthorDetails?: { _id?: string; Name?: string };
	/** The older shape — a whole contact document under `Author`. */
	Author?: {
		_id?: string;
		Name?: string;
		PersonInfo?: { Name?: { FullName?: string } };
	};
}

/** Attendee — HIGH sensitivity. Display only; never log or persist. */
/**
 * A carer attached to an attendee. The API already filters out deceased and
 * do-not-contact records, and sorts by CallOrder, so index 0 is Carer 1.
 */
export interface AttendeeCarer {
	/** Salutation plus full name, e.g. "Mrs Hardy Smith". */
	Name?: string;
	GivenName?: string;
	FamilyName?: string;
	Email?: string | null;
	Phone?: string | null;
}

/**
 * An attendee EXACTLY as `event/admin/{id}?attendees=1` returns it.
 *
 * This previously declared a flat, snake_case shape (`given_name`, `surname`,
 * `approval`, `form`, …) that the API never sends — that shape is what v2
 * builds client-side in EventAttendees.tsx after fetching. Reading it straight
 * off the response meant every field was undefined. The mapping to display
 * fields now lives in EventAttendeesTab.
 */
export interface Attendee {
	id: string;
	/**
	 * `"FamilyName, GivenName"`. Superseded by the two fields below — kept
	 * because the API still sends it and it is the fallback when a deployment
	 * predates them.
	 */
	Name?: string;
	/** The LEGAL given name. What she is called is `PreferredName`. */
	GivenName?: string;
	/**
	 * What she is actually called day to day, which is what the roll shows.
	 *
	 * The API falls this back to `GivenName` before sending, so an absent value
	 * means a deployment predating the field rather than a student without one.
	 */
	PreferredName?: string;
	FamilyName?: string;
	EdumateStudentNumber?: string;
	CompassGroup?: string;
	/** `StudentInfo.Type`, e.g. "Day Student" or "Boarder" — not a boolean. */
	Boarder?: string;
	Carers?: AttendeeCarer[];
	/**
	 * Whether this person is taking part or running the thing.
	 *
	 * The API reads it off the `EventRelation` edge and reports it rather than
	 * filtering on it, so it is ABSENT on events synced before the relation
	 * carried one. Undefined therefore means "not recorded", not "an ordinary
	 * attendee" — code that treats the two the same will call a supervisor a
	 * student on older events.
	 *
	 * This is where an event's owner comes from on a single-event read. On the
	 * list, `Supervisors` carries the same people already filtered; see
	 * `utils/eventOwner.ts`.
	 */
	Type?: AttendeeType | null;
	Attending?: string;
	ApprovedAt?: string;
	FormsSubmitted?: boolean;
	/** Free text, max 500 chars. HIGH sensitivity: never log or persist. */
	DietaryRequirements?: string | null;
}

/**
 * What an attendee is to the event: taking part, or supervising it.
 *
 * The same two values the API's `AttendeeType` accepts on the add path, so the
 * roll reads back exactly what this app writes.
 */
export type AttendeeType = "Attendee" | "Supervisor";

/**
 * The decisions the API accepts. Same two values v2's grid has always written,
 * so the portal reads back what it expects. Null clears it to undecided.
 */
export type AttendeeDecision = "Approved" | "Not Approved";

/** One page of `GET event/admin/{id}/attendees/`. */
export interface AttendeeListPage {
	EventId: string;
	Total: number;
	Page: number;
	Limit: number;
	Attendees: Attendee[];
}

/**
 * `POST event/admin/{id}/attendees/`.
 *
 * Ids only, never names — the caller supplied them, so echoing them back leaks
 * nothing it did not already hold. A partial result is normal: one unknown id
 * does not stop the rest being written.
 */
export interface AttendeeAddResult {
	EventId: string;
	Added: string[];
	AlreadyLinked: string[];
	/** No matching contact. Nothing was written for these. */
	Unknown: string[];
	/**
	 * A source system owns this roll, so the next sync may revert the change.
	 * See `Source` for which one.
	 */
	SyncManaged: boolean;
	/** MyPymble, Edumate or Clipboard. */
	Source: string;
}

/** `PATCH` / `DELETE` on one attendee. */
export interface AttendeeWriteResult {
	EventId: string;
	ContactId: string;
	Result: string;
	SyncManaged: boolean;
	Source: string;
}

/**
 * The things an event might need arranging: catering, signage, a parking
 * allocation. Ticked on the Requirements tab.
 *
 * A fixed catalogue rather than free text, because these are handed to the
 * teams who actually provide them — catering, grounds, IT — and a list they can
 * filter on is worth more than a sentence somebody has to read.
 *
 * The catalogue itself lives in the Dictionary collection under
 * `EventRequirements` and is fetched by useEventRequirements — adding one is a
 * data change, not a release.
 *
 */
export interface EventRequirement {
	id: string;
	label: string;
	description: string;
	/** Swatch for the icon tile. Blank falls back to a neutral tile. */
	colour: string;
	/** lucide glyph name, resolved by the tab. Unknown names fall back. */
	icon: string;
	/** Display order from the dictionary. */
	sort: number;
	/**
	 * The form asked when this requirement is ticked, in display order.
	 *
	 * Empty for most requirements, which keep the single free-text note. Where
	 * a team already runs an intake form for the work (AV's Freshservice
	 * catalogue item, for one), the dictionary entry repeats its questions here
	 * so the organiser answers them once, on the event, instead of filling the
	 * team's form separately. Data rather than code for the same reason the
	 * catalogue is: the next team's form should not need a release.
	 */
	fields: RequirementField[];
}

/**
 * The kinds of question a requirement form can ask.
 *
 * `info` asks nothing: it is guidance shown between questions, like the "these
 * spaces already have AV" paragraph on the AV form. It is plain text with an
 * optional link, never HTML — the dictionary is not a place to take markup
 * from.
 */
export type RequirementFieldType =
	| "text"
	| "paragraph"
	| "select"
	| "datetime"
	| "info";

/** One question (or one piece of guidance) on a requirement's form. */
export interface RequirementField {
	/** Stable key the answer is stored under. */
	id: string;
	/** The question, or an optional heading for an `info` block. */
	label: string;
	type: RequirementFieldType;
	required: boolean;
	/** Choices for `select`. Ignored for every other type. */
	options: string[];
	/** Help under the control. */
	hint: string;
	/** Body of an `info` block. */
	text: string;
	/** Optional link at the end of an `info` block. http(s) only. */
	url: string;
	linkLabel: string;
	/** `warning` for an `info` block that is a condition, not a tip. */
	tone: "default" | "warning";
	/**
	 * Shown only while the answer stored under `id` equals `equals`. Null for a
	 * field that is always shown.
	 */
	showIf: { id: string; equals: string } | null;
}

/**
 * One answer as stored on the event.
 *
 * `Label` and `Type` are copied from the field at the time of answering. The
 * request raised for the team is built from these, and the question's wording
 * belongs with the answer: a form edited in the dictionary next term must not
 * change what an old ticket says was asked.
 */
export interface EventRequirementAnswer {
	Id: string;
	Label: string;
	Type: Exclude<RequirementFieldType, "info">;
	/**
	 * The answer. A `datetime` answer is an ISO instant, same as the event's
	 * own start and end.
	 */
	Value: string;
}

/**
 * One requirement as stored on the event.
 *
 * An array of objects rather than the `Selected` ids + `Notes` map the tab keeps
 * in React state: two parallel structures can disagree, and neither has anywhere
 * to record who asked for the catering — the first question the catering team
 * has. The tab maps between the two shapes at its edges.
 *
 * `AddedAt` / `AddedBy` are read-only here. The API stamps them and refuses to
 * take them from the request, so sending them back on a round trip is harmless
 * but pointless.
 */
export interface EventRequirementSelection {
	Id: string;
	Note?: string | null;
	/**
	 * Answers to the requirement's form, for a requirement that has one.
	 *
	 * NEEDS pymble-apis TO ACCEPT IT. The API ignores unknown keys on a
	 * requirement rather than rejecting them, so until `RequirementUpdate` takes
	 * `Answers` a save sends them and the response comes back without them.
	 */
	Answers?: EventRequirementAnswer[] | null;
	AddedAt?: string;
	AddedBy?: { _id?: number | string; Name?: string };
	/**
	 * The request raised with the requirement's team, once one has been.
	 *
	 * Written by pymble-apis when the event is submitted for publishing, never
	 * by this app. The API carries it across a save by itself, so the draft
	 * passing it back untouched is fine but not required.
	 */
	Request?: EventRequirementRequest | null;
	/** Why the last attempt to raise the request failed. Cleared by a save. */
	RequestError?: { Reason?: string; At?: string } | null;
}

/**
 * A request raised in another system for one requirement.
 *
 * `System` names the provider (`freshservice` today), stored because the
 * catalogue's destination can change after the request was raised. `Url` is
 * null when the server cannot build one.
 *
 * `Status` is NOT WRITTEN BY THE API YET. The ticket's state lives in the
 * other system; until pymble-apis reads it back, the tab can only say the
 * request was raised.
 */
export interface EventRequirementRequest {
	System?: string;
	Id?: number | string;
	Ref?: string | null;
	Target?: number | string | null;
	Url?: string | null;
	RaisedAt?: string;
	Status?: string | null;
	Summary?: EventRequirementRequestSummary | null;
}

/**
 * The API's plain-language digest of the ticket's conversation so far.
 *
 * Generated server-side from the ticket's replies, so the organiser can see
 * where things stand without opening a service desk they may not have access
 * to. Only `State: "ready"` carries usable `Text`; any other state (still
 * generating, failed) is treated as no summary.
 *
 * `AwaitingOrganiser` is the one field that asks something of the reader:
 * when true, `NextStep` is what the team is waiting on them to do.
 * `SourceUpdatedAt` is when the ticket last changed, `GeneratedAt` when the
 * summary was written.
 */
export interface EventRequirementRequestSummary {
	State?: string;
	Text?: string | null;
	AwaitingOrganiser?: boolean;
	NextStep?: string | null;
	Replies?: number;
	SourceUpdatedAt?: string | null;
	GeneratedAt?: string | null;
}

/** A single event as returned by `event/admin/{id}`. */
/**
 * Who did something to an event, as the API stores it.
 *
 * `_id` is a ContactPersonal id and `Name` is resolved at WRITE time, not read
 * time — the API stamps the name into the document so the history stays
 * readable after someone leaves. That also means a document can carry the name
 * that lookup produced on the day, which for a while was the literal string
 * "Unknown"; see `originOf` in EventDetailsTab on why that is not displayed.
 */
export interface EventActor {
	_id?: string | null;
	Name?: string | null;
}

/**
 * Present on a single-event read when the event is one of a recurring series.
 * Computed by the API from the other events sharing its `MasterEventId`, so it
 * is never present for an event on its own. Read-only: it is not part of what
 * is saved (see `toWriteShape`).
 */
export interface EventSeries {
	MasterEventId: string | number;
	/** Events in the series, this one included. */
	Count: number;
	/** This event's place in the series by start time, 1-based. */
	Position: number;
	/** Whether the API will update the whole series from here. */
	Editable: boolean;
}

export interface EventDoc {
	_id: string;
	Series?: EventSeries;
	Title?: string;
	Category?: string;
	StartDateTime?: string;
	EndDateTime?: string;
	MasterEventId?: number;
	/** Edumate's numeric event id — used to build the medical report links. */
	EventId?: number;
	/** Presence of `Edumate` here is what makes the medical reports available. */
	ForeignKeys?: Record<string, unknown>;
	AdditionalNotes?: string | null;
	/* NULL, not just absent. The API stores an empty description as null rather
	   than omitting the key, so a type of `string | undefined` was a quiet
	   fiction — and the difference matters: writing `undefined` where the server
	   holds `null` reads as an edit to anything comparing the two. */
	Description?: { Long?: string | null; Short?: string | null };
	/* NO `Supervisors`. It was declared here, and it was never populated: the
	   key was a read-time construction of the legacy S1 function and S2 does
	   not send it. Anything reading it renders blank on every event, which is
	   the bug event-manager-v2's "Event Owner" field still has. Supervisors
	   come off `Attendees` by `Type` — see `utils/eventOwner.ts`. */
	Location?: EventLocation;
	Approval?: EventApproval;
	/** Chosen `EventTransport` dictionary Code and notes; null clears it, absent leaves it. */
	Transport?: {
		Code: string;
		Notes: string | null;
		AcknowledgementRequired: boolean;
		AcknowledgementText: string | null;
		/** Read-only: the API adds the dictionary label and description on read; never written. */
		Label?: string | null;
		Description?: string | null;
	} | null;
	/**
	 * Where the event sits in the publishing sign-off process.
	 *
	 * ABSENT on nearly every event, and that absence is meaningful: an event
	 * that was never submitted has no workflow at all, which is true of every
	 * timetable and co-curricular booking and of anything created here and not
	 * yet sent for sign-off. `null` is a workflow that was cleared. Only
	 * `published` means colleagues have released it.
	 *
	 * Just the status, not the steps and history — `event/admin/` returns the
	 * whole sub-document on every list row, but this is the only part the
	 * calendar reads (see eventFacets.isReleased). The full shape is
	 * `PublishWorkflow` in useEventPublish, which is also where the status
	 * vocabulary is declared, so a fifth status cannot be added there and
	 * silently mean nothing here.
	 */
	PublishWorkflow?: { Status?: PublishStatus | null };
	Banner?: EventBanner;
	/**
	 * The groups the event was built from, as IDS.
	 *
	 * event-manager-v2 sends `string[]` here and reads the names back from
	 * `Groups` below — the two halves of one thing, split across two fields by
	 * the API: this one is written, that one is expanded. Objects are accepted
	 * on read because this app spent a while writing them into this field.
	 */
	SelectedGroups?: (string | EventGroup)[];
	/**
	 * The same groups, expanded by the API into documents with `Label`, `Icon`
	 * and `MemberCount`. This is where a group's NAME comes from — v2 reads
	 * exactly this — and it is why a picker that reads names out of
	 * `SelectedGroups` shows a list of nameless rows.
	 */
	Groups?: EventGroup[];
	/**
	 * The event's own audience groups, as opposed to `SelectedGroups`, which is
	 * the list of groups the event was built from.
	 *
	 * `Attendees` is the students; `Parents` is their carers. These are what a
	 * notice or an app notification should target — pass the `_id` of each,
	 * not `GroupId`.
	 */
	EventGroups?: {
		Attendees?: EventAudienceGroup;
		Parents?: EventAudienceGroup;
	};
	/**
	 * Who created the event IN EVENT MANAGER, and when.
	 *
	 * Absent on almost everything: the API writes `CreatedBy` only on the create
	 * path here, and the overwhelming majority of events are synced in from
	 * Edumate or Clipboard, which set neither. `CreatedAt` is far more common —
	 * the sync stamps it — so the two are read separately rather than as a pair.
	 */
	CreatedBy?: EventActor | null;
	CreatedAt?: string | null;
	UpdatedAt?: string | null;
	/** The latest event check — see StoredEventReview. */
	Review?: StoredEventReview | null;
	Notices?: (string | EventNotice)[];
	Attendees?: Attendee[];
	/**
	 * The staff running the event, named by the LIST endpoint.
	 *
	 * This key used to be a trap and the note here said so: S1 built it at read
	 * time and S2 never sent it, so anything reading it rendered blank. It is
	 * real now, on `GET event/admin/` with `supervisors=1`, which resolves the
	 * `Supervisor`-typed EventRelation edges in two queries for the whole page.
	 *
	 * ONLY ON THE LIST. A single-event read does not carry it, and does not need
	 * to: it already enriches the whole roll, so the same people are in
	 * `Attendees` with `Type: "Supervisor"`. `supervisorNames` reads either —
	 * the rows are deliberately the same name shape — so the editor keeps
	 * deriving the owner from the roll and the calendar reads it straight off
	 * this key.
	 *
	 * Absent means it was not asked for. Empty means it was, and the event has
	 * no supervisor recorded — which is most of them, since a synced event
	 * predating the relation's `Type` has nothing to report.
	 */
	Supervisors?: Attendee[];
	/**
	 * When the permission was released to carers, or null if it has not been.
	 *
	 * The API derives `Approval.ShowPermission` from this on every update —
	 * non-null shows the permission in the parent portal, null hides it — so this
	 * one timestamp is the whole publish switch. Null, not just optional: the
	 * toggle has to be able to send an explicit null to withdraw it.
	 */
	ActiveAt?: string | null;
	/**
	 * What the event needs arranging. Absent and empty are different answers:
	 * omitting the key on a PUT leaves the stored list alone (which is how
	 * event-manager-v2's narrower payload avoids wiping it), while `[]` says the
	 * event needs nothing.
	 */
	/**
	 * What kind of event this is — "Curricular", "Co-Curricular", "Community"…
	 *
	 * Distinct from `Category`, which says where the event came FROM (Event
	 * Manager, Edumate, Clipboard). The type is what the sign-off chain is chosen
	 * by, so an event created here without one falls to the default chain.
	 */
	EventType?: string | null;
	/**
	 * Free-text labels staff put on an event so they can find it again.
	 *
	 * An OPEN vocabulary, and the only one on the document. The editor's field
	 * type-aheads against `Dictionary/EventTags`, but it accepts anything, and a
	 * tag nobody has used before is added to that catalogue by the API as the
	 * event saves — which is what lets the list converge without anyone curating
	 * it. So a tag here is NOT guaranteed to be in the catalogue: anything
	 * rendering these must read the stored values, never look them up.
	 *
	 * Distinct from `Requirements`, which is a fixed catalogue handed to the
	 * teams who provide the things in it, and from `EventType`, which is one
	 * value from a list the API routes approvals on. Tags decide nothing; they
	 * are how an event is found.
	 *
	 * Absent and empty differ, as they do for Requirements: omitting the key on
	 * a PUT leaves the stored tags alone (which is how event-manager-v2's
	 * narrower payload avoids wiping them), while `[]` clears them.
	 */
	Tags?: string[];
	Requirements?: EventRequirementSelection[];
	/**
	 * Visible OUTSIDE the school, to people who are not signed in at all.
	 *
	 * An open day, a public concert, a term date. Off is the default and the
	 * normal state: an event is seen by the people at the school it concerns.
	 *
	 * ⚠️ THIS IS THE ONLY FIELD ON THIS DOCUMENT THAT SENDS ANYTHING PAST THE
	 * FRONT GATE, which is why what travels with it is an allow-list rather
	 * than a projection with things removed: title, times, location, short
	 * description. Never `Attendees`, `SelectedGroups`, `AdditionalNotes`,
	 * `Requirements`, `Approval` or `CarerApprovals` — an event document holds
	 * student names, and the difference between "left out" and "not included"
	 * is the difference between a field added later being safe and being a
	 * breach. The rules are in `docs/public-events.md`.
	 *
	 * NOT a permission, and not the release switch. Carers of invited students
	 * get their permission through the sign-off chain either way; this adds an
	 * outside audience and takes nothing away from the inside one.
	 *
	 * STORED SINCE papi v2.44.0, as `EventUpdateRequest.is_public`. Omitting the
	 * key leaves the stored value alone — event-manager-v2 never sends it, and a
	 * field written unconditionally would run wrong in both directions for that
	 * caller. The API stamps `IsPublicSetBy` / `IsPublicSetAt` on a CHANGE only,
	 * and pins a single-event write on the state it read, so a save carrying a
	 * flag somebody else has since moved answers 409 rather than reinstating the
	 * old value under this saver's name.
	 *
	 * ⚠️ NOTHING SERVES IT OUTSIDE YET. The flag persists and the editor reads
	 * it back, but there is no unauthenticated route, so turning it on records
	 * the decision and publishes nothing. The public read path — an allow-list
	 * payload, not a projection — is still the work in `docs/public-events.md`.
	 *
	 * Absent means the same as false. There is no third state.
	 */
	IsPublic?: boolean;
	/**
	 * When the crew may start setting up, and when everything must be out.
	 *
	 * `HH:MM` AND NOTHING ELSE — NOT an instant, and the one field pair on this
	 * document that is not. A bump time is a call time: "four o'clock", read
	 * against the day the event runs. No date, so no timezone and nothing to
	 * convert; the value the time input emits is the value the API stores.
	 *
	 * A DATE-CARRYING VALUE IS REFUSED, not truncated — a 422 reading "Not a
	 * time of day — expected HH:MM". That is deliberate on the API's side: a
	 * client still sending the old ISO instant would otherwise go on working
	 * while quietly dropping the date, which is the whole of what the old shape
	 * said about the night before. This client sent instants until 2026-09-16.
	 *
	 * NEITHER IS CHECKED AGAINST THE EVENT'S OWN TIMES, at either end. A hall
	 * held from 6am for a 9am assembly is ordinary, and so is `01:00` against a
	 * concert finishing at 22:00 — an overnight pack-down, which a bare time
	 * cannot distinguish from a mistake. Where a day has to be chosen to draw
	 * something, the calendar reads the nearest occurrence outside the event;
	 * see `bumpBufferMinutes`.
	 *
	 * `null` clears one, and `""` is taken to mean the same. Both are
	 * distinguished from ABSENT, for the same reason `ActiveAt` is: the editor
	 * has to be able to remove a bump time that was set, and omitting the key
	 * on a PUT means "leave it alone".
	 *
	 * They stay inside the school: `publish_guard` keeps both off the
	 * carer-facing and public payloads, alongside `AdditionalNotes`. When a
	 * building is unlocked and who is in it is operational detail.
	 */
	BumpIn?: string | null;
	BumpOut?: string | null;
	/**
	 * Documents attached to the event — a risk assessment, a costing, a running
	 * sheet, a permission form.
	 *
	 * `EventFile` IS the platform's file shape, not a new one. The storage
	 * service hands this back from `storage/file/` and a pastoral note stores
	 * exactly the same object in its own `Files` array; keeping the keys
	 * identical is what lets one file viewer, one SAS call and one retention
	 * policy serve both.
	 *
	 * STORED SINCE papi v2.44.0, which landed alongside `IsPublic` above. This
	 * note said the opposite until then: `EventUpdateRequest` now declares
	 * `Files`, so a save keeps the list rather than leaving each blob in
	 * storage with nothing pointing at it. Omitted and empty are different
	 * answers, as with `Tags` — absent leaves the stored list alone, `[]`
	 * detaches everything. A `Url` is validated on the way in rather than
	 * trusted. See `docs/event-documents.md`.
	 */
	Files?: EventFile[];
}

/**
 * A stored file, exactly as `storage/file/` returns it.
 *
 * PascalCase because that is what the service sends, and a subset because this
 * app reads a subset: the full shape (archive dates, uploader detail,
 * ImageKit ids) is in the portal's `FileMetadata`. Anything added here must
 * keep that name, or the two stop being the same file.
 */
export interface EventFile {
	/** The original filename, which is what a person recognises it by. */
	Name: string;
	/** Where the blob is. Needs a SAS appended when `Secure` — see useEventFiles. */
	Url: string;
	/** The name in blob storage, unique where `Name` is not. */
	InternalName?: string;
	Container?: string;
	/** MIME type, as the service determined it. Often absent for Office files. */
	Type?: string;
	Size?: number;
	Secure?: boolean;
	Private?: boolean;
	Tags?: string[];
	/** Set by the service for private files; not a SAS. */
	Token?: string;
	UploadedAt?: string;
	/** Edumate contact id, stamped by the service. */
	UploadedBy?: number;
}

/** Shape `useEventManager`'s list query emits (event doc + Mobiscroll fields). */
export interface CalendarEvent extends EventDoc {
	id: string;
	title: string;
	start?: string;
	end?: string;
	event_type: string;
	Source: string;
	location: string;
	color: string;
	/**
	 * `BumpIn` and `BumpOut` as Mobiscroll reads them: MINUTES of buffer either
	 * side of the event, rather than the stored time of day.
	 *
	 * Mobiscroll's own names, deliberately — these are its props, passed
	 * straight through on the event object, and renaming them would mean
	 * mapping them back at the calendar. `undefined` where there is no band to
	 * draw; see `bumpBufferMinutes`, which also settles which DAY a bare
	 * `HH:MM` is measured against.
	 *
	 * Only the schedule views (Week, Day) draw them. See `toCalendarEvent`.
	 */
	bufferBefore?: number;
	bufferAfter?: number;
}

/**
 * Left-sidebar filter state on the calendar page.
 *
 * `categories` is the odd one out: it is sent to the API as a request
 * parameter, so an EMPTY list means "ask for nothing" and the calendar is
 * blank. Every other list here is a client-side facet over what came back,
 * where empty means "no constraint" — see eventFacets.matchesFilters.
 */
export interface EventFilterState {
	searchText: string;
	categories: string[];
	/**
	 * ONE `EventType`, or empty for no constraint; `__none` for events that have
	 * none of their own.
	 *
	 * A list until the rail's select replaced its multi-select. Nothing needed
	 * more than one: the types are alternatives — a Curricular event is not also
	 * Community — so the only multi-selections anyone made were either one value
	 * or, occasionally, every value, which is the same as none. What the list
	 * did add was a tag strip under the control reading "No selections"
	 * whenever nothing was chosen, and a filter that announces its own
	 * emptiness is noise in a rail this narrow.
	 */
	eventType: string;
	/** Audience group `_id`s. */
	groups: string[];
	/**
	 * Tags, matched case-insensitively by the API; `__none` for untagged events.
	 *
	 * The SECOND server-side facet, after `categories` — it is a request
	 * parameter and part of the query key, so ticking one refetches the range
	 * rather than filtering what is already on screen. That is why an empty list
	 * here means "no constraint" while an empty `categories` means "ask for
	 * nothing": tags NARROW a result the API would otherwise return whole.
	 */
	tags: string[];
	/**
	 * Hide everything the sign-off process has not released.
	 *
	 * Not submitted, in review, sent back or declined — the same reading as the
	 * API's own `unapproved` filter and the sign-off screen's "Not approved
	 * only" switch, so the word means one thing in both places.
	 *
	 * Client-side, over what came back, because the status arrives on every row
	 * already (see eventFacets.isReleased): nothing has to be re-fetched to turn
	 * it on. Default OFF, so the calendar opens showing everything it always
	 * has.
	 *
	 * ONLY MY PYMBLE EVENTS. Edumate and Clipboard events are always shown: they
	 * arrive from systems that have no sign-off chain, so every one of them is
	 * unreleased by this test and the filter would empty the calendar rather than
	 * narrow it. The question this answers is "which of the events WE made are
	 * not out yet", and that is the only source it can be asked of.
	 */
	hideUnapproved: boolean;
	/**
	 * Keep only events that fall inside the school day.
	 *
	 * OVERLAP, not containment — an excursion that leaves before the first bell
	 * and returns after the last is still a school-day event, and so is an
	 * all-day one. The window and the reasoning are in `utils/schoolDay.ts`,
	 * which is also the only place the hours are written down.
	 *
	 * Unlike `hideUnapproved` this applies to EVERY source. The question is
	 * about the clock, and an Edumate lesson answers it as well as anything
	 * created here.
	 *
	 * Client-side, over what came back — the API has no time-of-day filter — and
	 * default OFF, like every other filter here.
	 */
	schoolDayOnly: boolean;
	/**
	 * Keep only events that ask carers for permission: `Approval.Required`.
	 *
	 * Client-side over what came back, and default OFF. An event with no
	 * Approval block (Edumate and Clipboard rows carry none) does not ask for
	 * permission, so it is hidden when this is on.
	 */
	permissionOnly: boolean;
}

/**
 * Events carrying no tags, as the API's own `tags=__none`.
 *
 * Lives here rather than beside the facets because two modules need it and
 * neither should have to import the other: `eventFacets` buckets untagged
 * events under it when tallying, and `eventFilterStorage` strips it out of a
 * restored cookie. Declaring it in the facets made that pair circular.
 *
 * It is NOT a row in the filter rail. There was one — "which events has nobody
 * labelled yet" is a real question while tagging is being rolled out — and it
 * was removed on request.
 */
export const UNTAGGED = "__none";

/**
 * The event sources the filter rail offers, which are also what the API's
 * `categories` parameter takes.
 *
 * THESE ARE SOURCE IDS, NOT `Category` VALUES. The API filters on an event's
 * `ForeignKeys`, not on the `Category` label it computes afterwards, which is
 * what lets a label be corrected without breaking a filter.
 *
 * `defaultOn` is what a new person sees. Clipboard starts unticked: its ~1,195
 * co-curricular sessions a week carry no approval block, and 94% of them have
 * one student or none, so they are individual music lessons rather than anything
 * anyone manages here.
 *
 * LISS IS DELIBERATELY ABSENT. Edval timetable and room bookings are hidden by
 * the API's default and there is no toggle for them, because there is nothing to
 * do with one if you found it: ~350 a week across only 49 distinct titles, none
 * with a banner or an approval block, and anything you did attach would be wiped
 * by the next sync. The API still accepts `categories=liss` for inspection; no
 * UI asks for it.
 *
 * EVERY ENTRY HERE MUST BE CREATABLE — the create dialog offers this same list,
 * and the API's EVENT_CALENDARS rejects anything else with a 422. If a
 * non-creatable source is ever added, split the list rather than widening it.
 */
/**
 * The source id for events created in this app — "Event Manager" on the rail.
 *
 * Named because it is not just one row of the catalogue: it is the only source
 * whose events go through the publishing sign-off chain, so it is what the
 * "Hide unapproved" filter applies to (see eventFacets.isOwnEvent). Edumate and
 * Clipboard events arrive from systems with no notion of it.
 *
 * The value is what `useEventManager.toCalendarEvent` puts in `Source`, which
 * is also the fallback it uses for a document with no `Category` at all.
 */
export const MANUAL_SOURCE = "manual";

export const EVENT_CATEGORIES: {
	id: string;
	label: string;
	color: string;
	defaultOn: boolean;
}[] = [
	{ id: MANUAL_SOURCE, label: "Event Manager", color: "#002f67", defaultOn: true },
	{ id: "edumate", label: "Edumate", color: "#26afd1", defaultOn: true },
	{ id: "clipboard", label: "Clipboard", color: "#26d153", defaultOn: false },
];

/**
 * `POST event/admin/{id}/review/`: the pre-flight check an organiser runs on
 * the SAVED event before sending it for sign-off.
 *
 * The API builds the issues from fixed checks (clashes with other events,
 * assessments and sport, and details not filled in yet); an AI pass then
 * orders and rewords them and may add a few of its own. It cannot remove one.
 *
 * COUNTS, NEVER NAMES. `Facts` holds how many people are affected, not who.
 */
export type ReviewSeverity = "high" | "medium" | "low";

export interface ReviewIssue {
	/** "sport_clash", "busy_requirement_same_day:av", "ai_observation", ...
	    A key only: the wording comes from Title, Detail and Suggestion. */
	Code: string;
	Severity: ReviewSeverity;
	Title: string;
	Detail: string;
	Suggestion?: string | null;
}

export interface ReviewFacts {
	StudentCount: number;
	StaffCount: number;
	/** The roll was too large to read in full, so counts cover part of it. */
	AttendeesTruncated: boolean;
	OtherEventsSameDay: number;
	RequirementLoad: {
		Code: string;
		Name: string;
		SameDay: number;
		NextDay: number;
	}[];
	AssessmentsSameDay: number;
	AssessmentsNextDay: number;
	SportClash: number;
	SportTight: number;
}

export interface EventReview {
	EventId: string;
	/** In the server's priority order. The review panel groups them by
	    severity but keeps this order within each group. */
	Issues: ReviewIssue[];
	/** The reply to `Question`, when one was asked. */
	Answer: string | null;
	/** False when the model was off, slow or failed: fixed wording only. */
	AiUsed: boolean;
	Facts: ReviewFacts;
	GeneratedAt: string;
	/** The event's `UpdatedAt` when the check read it. Differs from the
	    event's own once it has been saved since: the check is out of date. */
	EventUpdatedAt?: string | null;
	/** Whether the API kept this as the event's `Review`. False when the
	    event was saved while the check ran; the result is still right for
	    the version it checked. */
	Saved?: boolean;
}

/**
 * The latest check as the API keeps it on the event, under `Review`.
 *
 * The check's response less `Answer`, which answered one person's question.
 * Only the single-event admin read carries it; list reads leave it out, and
 * the event update ignores it, so the editor sending it back is harmless.
 * Storing one is not an edit: it moves no `UpdatedAt`.
 */
export interface StoredEventReview {
	Issues: ReviewIssue[];
	AiUsed: boolean;
	Facts: ReviewFacts;
	GeneratedAt: string;
	EventUpdatedAt?: string | null;
	ReviewedBy?: EventActor | null;
}

/** Either shape, as the editor shows it: this session's check or the stored one. */
export type ShownEventReview = Omit<EventReview, "EventId" | "Answer"> & {
	Answer?: string | null;
	ReviewedBy?: EventActor | null;
};
